import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase-config';
import { api } from '../services/firebaseFunctions';
import { useIsMobile } from '../utils/isMobile';
import { recordRoleSelections } from '../utils/roleFrequency';
import SignupRequestManagerDesktop from './SignupRequestManagerDesktop';
import SignupRequestManagerMobile from './SignupRequestManagerMobile';

const CREATE_NEW_COMPANY = '__create_new__';

function SignupRequestManager() {
  const isMobile = useIsMobile();

  const [pendingRequests, setPendingRequests] = useState([]);
  const [historyRequests, setHistoryRequests] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  const [reviewDialog, setReviewDialog] = useState({ open: false, request: null });
  const [reviewData, setReviewData] = useState({ displayName: '', company: null, newCompanyName: '', roles: [] });
  const [reviewErrors, setReviewErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [rejectDialog, setRejectDialog] = useState({ open: false, request: null });
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    loadReferenceData();

    const pendingQuery = query(
      collection(db, 'signupRequests'),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'asc')
    );
    const unsubPending = onSnapshot(pendingQuery, (snapshot) => {
      setPendingRequests(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (err) => {
      console.error('Error listening to pending requests:', err);
      setError('Failed to load pending requests');
      setLoading(false);
    });

    const historyQuery = query(
      collection(db, 'signupRequests'),
      where('status', 'in', ['approved', 'rejected']),
      orderBy('reviewedAt', 'desc'),
      limit(50)
    );
    const unsubHistory = onSnapshot(historyQuery, (snapshot) => {
      setHistoryRequests(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });

    return () => {
      unsubPending();
      unsubHistory();
    };
  }, []);

  const loadReferenceData = async () => {
    try {
      const [companiesData, rolesData] = await Promise.all([api.listCompanies(), api.listRoles()]);
      setCompanies(companiesData);
      setRoles(rolesData);
    } catch (err) {
      console.error('Error loading reference data:', err);
      setError('Failed to load companies/roles');
    }
  };

  const handleOpenReview = (request) => {
    const matchingCompany = companies.find(
      (c) => c.name.toLowerCase() === request.companyName?.toLowerCase()
    );
    setReviewData({
      displayName: request.displayName || '',
      company: matchingCompany || null,
      newCompanyName: matchingCompany ? '' : request.companyName || '',
      roles: [],
    });
    setReviewErrors({});
    setReviewDialog({ open: true, request });
  };

  const handleCloseReview = () => {
    setReviewDialog({ open: false, request: null });
    setReviewErrors({});
  };

  const getCompanyOptions = () => {
    const opts = [...companies];
    if (reviewData.newCompanyName && !companies.find(
      (c) => c.name.toLowerCase() === reviewData.newCompanyName.toLowerCase()
    )) {
      opts.push({
        id: CREATE_NEW_COMPANY,
        name: `+ Create new company: ${reviewData.newCompanyName}`,
        _isNew: true,
        _newName: reviewData.newCompanyName,
      });
    }
    return opts;
  };

  const handleApprove = async () => {
    const errors = {};
    if (!reviewData.company) errors.company = 'Company is required';
    if (!reviewData.roles.length) errors.roles = 'At least one role is required';
    if (Object.keys(errors).length > 0) { setReviewErrors(errors); return; }

    setSubmitting(true);
    setError(null);
    try {
      const payload = { requestId: reviewDialog.request.id, roles: reviewData.roles };
      if (reviewData.company.id === CREATE_NEW_COMPANY) {
        payload.newCompanyName = reviewData.company._newName;
      } else {
        payload.companyId = reviewData.company.id;
      }
      await api.approveSignupRequest(payload);
      setSuccess('Approved — welcome email sent.');
      setTimeout(() => setSuccess(null), 3000);
      handleCloseReview();
      loadReferenceData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenReject = (request) => {
    setRejectReason('');
    setRejectDialog({ open: true, request });
  };

  const handleReject = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await api.rejectSignupRequest(rejectDialog.request.id, rejectReason || null);
      setSuccess('Request rejected.');
      setTimeout(() => setSuccess(null), 3000);
      setRejectDialog({ open: false, request: null });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleToggle = (roleId) => {
    const adding = !reviewData.roles.includes(roleId);
    if (adding) recordRoleSelections([roleId]);
    setReviewData((prev) => ({
      ...prev,
      roles: adding
        ? [...prev.roles, roleId]
        : prev.roles.filter((id) => id !== roleId),
    }));
    setReviewErrors((prev) => ({ ...prev, roles: '' }));
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffMins = Math.floor((new Date() - date) / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const sharedProps = {
    pendingRequests,
    historyRequests,
    companies,
    roles,
    loading,
    error,
    success,
    setError,
    tabValue,
    setTabValue,
    reviewDialog,
    reviewData,
    setReviewData,
    reviewErrors,
    setReviewErrors,
    submitting,
    rejectDialog,
    setRejectDialog,
    rejectReason,
    setRejectReason,
    handleOpenReview,
    handleCloseReview,
    handleApprove,
    handleRoleToggle,
    handleOpenReject,
    handleReject,
    getCompanyOptions,
    formatRelativeTime,
    CREATE_NEW_COMPANY,
  };

  return isMobile
    ? <SignupRequestManagerMobile {...sharedProps} />
    : <SignupRequestManagerDesktop {...sharedProps} />;
}

export default SignupRequestManager;
